import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CiBallotService } from '../../shared/services/ci-ballot.service';
import { filter, of, Subscription, switchMap, take } from 'rxjs';
import { ICommitteeInternalBallotDto } from '../../shared/models/ci/committee-internal-ballot-dto';
import { IBallotInternalHistory } from '../../shared/models/ballot-internal-history/ballot-internal-history';
import { ICommitteeInternalBallotSummaryDto } from '../../shared/models/ci/committee-internal-ballot-summary-dto';
import { UnifiedBallotStatus } from '../../../../shared/enums/unified-ballot-status-enum';
import { IUser } from '../../../../shared/models/user';
import { AuthService } from '../../../../shared/services/auth.service';
import { UserUtility } from '../../../../shared/utilities/user-utility';

@Component({
    selector: 'app-ci-details',
    templateUrl: './ci-details.component.html',
    styleUrl: './ci-details.component.scss',
    standalone: false
})
export class CiDetailsComponent implements OnInit, OnDestroy {
  currentUser: IUser | null = null;
  subscriptions: Subscription = new Subscription();
  ciBallotDetailDto: ICommitteeInternalBallotDto | null = null;
  loadingBallotDetail: boolean = false;
  selectedTab: string = '';
  initialTabId: string = 'ballotSummary';
  isInternalUser: boolean = false;
  ballotInternalHistories: IBallotInternalHistory[] = [];
  visibleTabs = [
    { id: 'ballotSummary', label: 'Ballot Summary' },
    { id: 'viewVotes', label: 'View Votes' },
  ];

  constructor(
    private route: ActivatedRoute,
    private ciBallotService: CiBallotService,
    public _authService: AuthService
  ){
    this.initUser();
  }

  initUser() {
    const currentUserSubscription = this._authService.currentUserSubject
      .pipe(
        filter((user) => !!user),
        take(1)
      )
      .subscribe((user) => {
        this.currentUser = user;
        this.isInternalUser = UserUtility.isInternalUser(user!);
        this.updateVisibleTabs();
        console.log(`Is Internal User: ${this.isInternalUser}`);
      });
    this.subscriptions.add(currentUserSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.setupGetCiBallotDetailSubscription();
  }

  setupGetCiBallotDetailSubscription(ballotId?: string): void {
    let obs$ = this.route.params.pipe(
      switchMap(params => {
        const id = ballotId??params['id'];
        this.loadingBallotDetail = true;
        this.ciBallotDetailDto = null;

        if (!id) {
          this.loadingBallotDetail = false;
          this.ciBallotDetailDto = null;
          return of(null);
        }

        return this.ciBallotService.getCiBallotDetails(id);
      })
    );

    this.subscriptions.add(obs$.subscribe({
      next: (response) => {
        this.loadingBallotDetail = false;
        if (!response || !response.success) {
          console.log(`Error getting CI ballot details: ${response?.message}`);
          return;
        }

        if (response.success) {
          this.ciBallotDetailDto = response.content;
          this.ballotInternalHistories = this.generateBallotInternalHistory(
            this.ciBallotDetailDto.ballotSummary
          );
          this.updateVisibleTabs();
          console.log(`this ciBallot Dto: `, this.ciBallotDetailDto);
        }
      },
      error: (error) => {
        this.loadingBallotDetail = false;
        console.log(`Error getting CI ballot details: ${error}`);
      }
    }));
  }

  private updateVisibleTabs(): void {
    const baseTabs = [
      { id: 'ballotSummary', label: 'Ballot Summary' },
      { id: 'viewVotes', label: 'View Votes' },
    ];

    const isFinalised =
      this.ciBallotDetailDto?.ballotSummary?.statusDisplayText?.toLowerCase() ===
      UnifiedBallotStatus[UnifiedBallotStatus.finalised];

    this.visibleTabs =
      this.isInternalUser && isFinalised
        ? [...baseTabs, { id: 'internalHistory', label: 'Internal History' }]
        : baseTabs;
  }

  refreshCIBBallot(ballotId: string) {
    this.setupGetCiBallotDetailSubscription(ballotId);
  }

  generateBallotInternalHistory(
      detail: ICommitteeInternalBallotSummaryDto | null
    ): IBallotInternalHistory[] {
      if (!detail) {
        return [];
      }

      const name =
          detail.finalisedByName?.trim() ||
          detail.finalisedByEmail?.trim() ||
          '-';

      return [
        {
          name,
          status: 'Finalised',
          date: detail.finalisedTime ? new Date(detail.finalisedTime) : null,
          comments: '',
        },
      ];
    }


  onTabChange(tabId: string) {
    this.selectedTab = tabId;
  }
}
